const DB_NAME = "zoning-cache";
const DB_VERSION = 1;
const STORE = "pages";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function clearAllPages() {
  const db = await openDb();
  return await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(STORE).clear();
  });
}

export async function getCachedPage(key: string) {
  const db = await openDb();
  return await new Promise<any | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function putCachedPage(record: any) {
  const db = await openDb();
  return await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(STORE).put(record);
  });
}

// ==================== ZONING DOCUMENT CACHING ====================

/**
 * Generates a unique cache key for a zoning page
 * Format: "zoning_{taskId}_page_{pageIndex}"
 */
function getZoningPageKey(taskId: string, pageIndex: number): string {
  return `zoning_${taskId}_page_${pageIndex}`;
}

/**
 * Cache a single zoning page with timestamp
 * @param taskId - Unique identifier for the task
 * @param pageIndex - Index of the page (0-based)
 * @param imageUrl - Blob URL of the image
 * @param jsonData - JSON data (Textract response)
 */
export async function cacheZoningPage(
  taskId: string,
  pageIndex: number,
  imageUrl: string,
  jsonData: any
): Promise<void> {
  const key = getZoningPageKey(taskId, pageIndex);
  const record = {
    key,
    taskId,
    pageIndex,
    imageUrl,
    jsonData,
    timestamp: Date.now(),
  };
  await putCachedPage(record);
}

/**
 * Get a cached zoning page
 * @param taskId - Unique identifier for the task
 * @param pageIndex - Index of the page (0-based)
 * @param maxAge - Maximum age in milliseconds (default: 24 hours). Returns null if expired.
 * @returns Cached page data or null if not found/expired
 */
export async function getCachedZoningPage(
  taskId: string,
  pageIndex: number,
  maxAge: number = 24 * 60 * 60 * 1000 // 24 hours default
): Promise<{ imageUrl: string; jsonData: any; timestamp: number } | null> {
  const key = getZoningPageKey(taskId, pageIndex);
  const record = await getCachedPage(key);

  if (!record) return null;

  // Check if cache is expired
  const age = Date.now() - record.timestamp;
  if (age > maxAge) {
    // Cache expired, delete it
    await deleteZoningPage(taskId, pageIndex);
    return null;
  }

  return {
    imageUrl: record.imageUrl,
    jsonData: record.jsonData,
    timestamp: record.timestamp,
  };
}

/**
 * Delete a specific zoning page from cache
 */
async function deleteZoningPage(taskId: string, pageIndex: number): Promise<void> {
  const db = await openDb();
  const key = getZoningPageKey(taskId, pageIndex);
  return await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(STORE).delete(key);
  });
}

/**
 * Clear all cached pages for a specific task
 * @param taskId - Unique identifier for the task to clear
 */
export async function clearZoningCache(taskId: string): Promise<void> {
  const db = await openDb();
  return await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const request = store.openCursor();
    const keysToDelete: string[] = [];

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        // Check if this record belongs to the task
        if (cursor.value.taskId === taskId) {
          keysToDelete.push(cursor.value.key);
        }
        cursor.continue();
      } else {
        // Done scanning, now delete all matching records
        const deleteTx = db.transaction(STORE, "readwrite");
        deleteTx.oncomplete = () => resolve();
        deleteTx.onerror = () => reject(deleteTx.error);

        const deleteStore = deleteTx.objectStore(STORE);
        keysToDelete.forEach((key) => deleteStore.delete(key));
      }
    };
    request.onerror = () => reject(request.error);
  });
}